import "../../assets/css/RightPanel.css";
import "../../assets/css/AdminTool.css";

import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";

import useAccountDeletionMonitoring from "../../hooks/useAccountDeletionMonitoring";

const { Text } = Typography;

const DEFAULT_ACCOUNT_DELETION_PAGE = 1;
const DEFAULT_ACCOUNT_DELETION_PAGE_SIZE = 10;

const DELETION_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "REQUESTED", label: "REQUESTED" },
  { value: "IN_PROGRESS", label: "IN_PROGRESS" },
  { value: "COMPLETED", label: "COMPLETED" },
  { value: "PARTIAL_FAILURE", label: "PARTIAL_FAILURE" },
  { value: "FAILED", label: "FAILED" },
];

const DELETION_STATUS_TAG_COLORS = {
    FAILED: "error",
    PARTIAL_FAILURE: "warning",
    COMPLETED: "success",
    IN_PROGRESS: "processing",
    REQUESTED: "gold",
};

function maskOsyncIdForAdminList(osyncId) {
  if (osyncId == null || osyncId === "") {
    return "—";
  }
  const s = String(osyncId);
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function maskEmailForAdminList(email) {
  if (email == null || email === "") {
    return "—";
  }
  const s = String(email);
  const at = s.indexOf("@");
  const local = s.slice(0, at);
  const domain = s.slice(at + 1);
  if (local.length <= 1) {
    return `*@${domain}`;
  }
  return `${local[0]}***@${domain}`;
}

function safeJsonStringify(value) {
  if (value === undefined || value === null) {
    return "";
  }
  try {
    return typeof value === "string" ? value : JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function truncateText(text, maxLen) {
  if (text == null || text === "") {
    return "";
  }
  const s = String(text);
  return s.length <= maxLen ? s : `${s.slice(0, maxLen)}…`;
}

function getDeletionErrorHint(record) {
  if (record.failureMessage) {
    return truncateText(record.failureMessage, 140);
  }
  const failed = record.failedRepositories;
  if (Array.isArray(failed) && failed.length > 0) {
    const first = failed[0];
    if (first && typeof first === "object") {
      const repo = first.repository || "";
      const err = first.error || "";
      return truncateText(`${repo}${repo && err ? ": " : ""}${err}`, 140);
    }
  }
  if (record.hasFailures) {
    return "Failure recorded — see details";
  }
  return "—";
}

function getStatusTagColor(status) {
  return DELETION_STATUS_TAG_COLORS[status] || "default";
}

function formatRelativeUpdatedTime(iso) {
  if (!iso) {
    return "—";
  }
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  const diffSec = Math.round((Date.now() - parsed) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (abs < 60) {
    return rtf.format(-diffSec, "second");
  }
  if (abs < 3600) {
    return rtf.format(-Math.round(diffSec / 60), "minute");
  }
  if (abs < 86400) {
    return rtf.format(-Math.round(diffSec / 3600), "hour");
  }
  return rtf.format(-Math.round(diffSec / 86400), "day");
}

async function copyToClipboard(text, successLabel) {
  try {
    await navigator.clipboard.writeText(text);
    message.success(successLabel || "Copied");
  } catch {
    message.error("Copy failed");
  }
}

function CompletedRepositories({ completedRepositories }) {
  return !Array.isArray(completedRepositories) || completedRepositories.length === 0 ? (
    <Text type="secondary">No completed repositories yet.</Text>
  ) : (
    <div className="d-flex flex-wrap gap-2">
      {completedRepositories.map((repositoryName) => (
        <Tag color="success" key={`completed-${repositoryName}`}>
          {repositoryName}
        </Tag>
      ))}
    </div>
  );
}

function FailedRepositories({ failedRepositories }) {
  return !Array.isArray(failedRepositories) || failedRepositories.length === 0 ? (
    <Text type="secondary">No failed repositories.</Text>
  ) : (
    <div className="d-flex flex-column gap-2">
      {failedRepositories.map((failedRepository, index) => {
        const repositoryName = failedRepository?.repository || "UnknownRepository";
        const failureMessage = failedRepository?.error || "No failure message";
        return (
          <div className="hs-border-light-red hs-border-8 hs-bg-light-red p-2 d-flex flex-column gap-1" key={`failed-${repositoryName}-${index}`}>
            <Text strong>{repositoryName}</Text>
            <Text type="danger">{failureMessage}</Text>
          </div>
        );
      })}
    </div>
  );
}

function DetailDrawerContent({ record }) {
  return record ? (
    <div className="account-deletion-drawer-body">
      <Descriptions size="small" column={1} bordered className="mb-3">
        <Descriptions.Item label="Osync ID">
          <Text>{record.osyncId}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={getStatusTagColor(record.status)}>{record.status || "—"}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Account email">{record.accountEmail || "—"}</Descriptions.Item>
        <Descriptions.Item label="Failure message">{record.failureMessage || "—"}</Descriptions.Item>
      </Descriptions>
      <Descriptions title="Repositories" size="small" column={1} bordered className="mb-3">
        <Descriptions.Item label="Completed">
          <CompletedRepositories completedRepositories={record.completedRepositories} />
        </Descriptions.Item>
        <Descriptions.Item label="Failed">
          <FailedRepositories failedRepositories={record.failedRepositories} />
        </Descriptions.Item>
      </Descriptions>
      <Descriptions title="Technical" size="small" column={1} bordered>
        <Descriptions.Item label="Dashboard summary">
          <pre className="hs-max-h-250-px overflow-auto hs-text-white-space-pre-wrap hs-overflow-wrap-break-word hs-fs-12 mb-0">{safeJsonStringify(record.dashboardSummary)}</pre>
        </Descriptions.Item>
        <Descriptions.Item label="Redis key">{record.redisKey || "—"}</Descriptions.Item>
        <Descriptions.Item label="Environment">{record.environment || "—"}</Descriptions.Item>
        <Descriptions.Item label="Last report">
          <pre className="hs-max-h-250-px overflow-auto hs-text-white-space-pre-wrap hs-overflow-wrap-break-word hs-fs-12 mb-0">{safeJsonStringify(record.lastReport)}</pre>
        </Descriptions.Item>
      </Descriptions>
    </div>
  ) : null;
}

export default function AccountDeletionStatusContainer() {
  const { fetchRecentDeletionStatuses, fetchDeletionStatusByOsyncId, isLoading } = useAccountDeletionMonitoring();
  const [recentRows, setRecentRows] = useState([]);
  const [accountDeletionStatusTotalCount, setAccountDeletionStatusTotalCount] = useState(0);
  const [accountDeletionTablePage, setAccountDeletionTablePage] = useState(DEFAULT_ACCOUNT_DELETION_PAGE);
  const [accountDeletionTablePageSize, setAccountDeletionTablePageSize] = useState(
    DEFAULT_ACCOUNT_DELETION_PAGE_SIZE,
  );
  const [osyncIdLookup, setOsyncIdLookup] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQueryDraft, setSearchQueryDraft] = useState("");
  const [searchQueryApplied, setSearchQueryApplied] = useState("");
  const [lookupDrawerOpen, setLookupDrawerOpen] = useState(false);
  const [lookupDeletionStatus, setLookupDeletionStatus] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const loadRecentPage = useCallback(
    async (page, pageSize, overrides = {}) => {
      const statusValue = overrides.status !== undefined ? overrides.status : statusFilter;
      const qValue = overrides.q !== undefined ? overrides.q : searchQueryApplied;
      const resp = await fetchRecentDeletionStatuses(page, pageSize, { status: statusValue, q: qValue });
      if (!resp) {
        return;
      }
      const items = resp.items || [];
      setAccountDeletionStatusTotalCount(resp?.total ?? items?.length);
      setAccountDeletionTablePage(resp?.page ?? page);
      setAccountDeletionTablePageSize(resp?.pageSize ?? pageSize);
      setRecentRows(
        items.map((row, index) => ({
          key: `${row.osyncId || "unknown"}-${(page - 1) * pageSize + index}`,
          ...row,
        })),
      );
    },
    [fetchRecentDeletionStatuses, statusFilter, searchQueryApplied],
  );

  useEffect(() => {
    loadRecentPage(DEFAULT_ACCOUNT_DELETION_PAGE, DEFAULT_ACCOUNT_DELETION_PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountDeletionTableChange = (page, pageSize) => {
    loadRecentPage(page, pageSize);
  };

  const applyFilters = () => {
    setSearchQueryApplied(searchQueryDraft);
    loadRecentPage(DEFAULT_ACCOUNT_DELETION_PAGE, accountDeletionTablePageSize, {
      q: searchQueryDraft,
    });
  };

  const handleRefreshRecent = () => {
    loadRecentPage(accountDeletionTablePage, accountDeletionTablePageSize);
  };

  const openDetailDrawer = (record) => {
    setDetailRecord(record);
    setDetailDrawerOpen(true);
  };

  const handleLookupByOsyncId = async () => {
    const trimmed = (osyncIdLookup || "").trim();
    if (!trimmed) {
      message.warning("Enter an osyncId");
      return;
    }
    const payload = await fetchDeletionStatusByOsyncId(trimmed);
    if (!payload) {
      return;
    }
    if (payload.responseStatus === 404) {
      setLookupDeletionStatus(null);
      setLookupDrawerOpen(true);
      message.info("No deletion status found for this osyncId (expired TTL or never recorded).");
      return;
    }
    if (payload.responseStatus !== 200) {
      message.error(payload.responseMessage || "Lookup failed");
      return;
    }
    setLookupDeletionStatus(payload.deletionStatus || null);
    setLookupDrawerOpen(true);
  };

  const recentColumns = [
    {
      title: "Osync ID",
      dataIndex: "osyncId",
      key: "osyncId",
      ellipsis: true,
      width: 200,
      render: (value) => {
        const display = maskOsyncIdForAdminList(value);
        return (
          <Space
            size={4}
            wrap
            onClick={(e) => {
              copyToClipboard(value, "Full osync ID copied");
              e.stopPropagation();
            }}
            style={{ cursor: "pointer" }}
          >
            <Tooltip title="Click to copy full osync ID">
              <span>{display}</span>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "accountEmail",
      key: "accountEmail",
      width: 150,
      ellipsis: true,
      render: (value) => {
        const display = maskEmailForAdminList(value);
        return (
          <Space
            size={4}
            wrap
            onClick={(e) => {
              copyToClipboard(value, "Full email copied");
              e.stopPropagation();
            }}
            style={{ cursor: "pointer" }}
          >
            <Tooltip title="Click to copy full email">
              <span>{display}</span>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (value) => (
        <Tooltip title={value || ""}>
          <span>{formatRelativeUpdatedTime(value)}</span>
          {value ? (
            <Text type="secondary" className="hs-fs-11 d-block mt-1">
              {" "}
              ({value})
            </Text>
          ) : null}
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (value) =>
        value ? <Tag color={getStatusTagColor(value)}>{value}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: "Error / issue",
      key: "errorHint",
      ellipsis: true,
      width: 220,
      render: (_, record) => (
        <Tooltip title={getDeletionErrorHint(record)}>
          <Text type={record.hasFailures || record.status === "FAILED" ? "danger" : "secondary"}>
            {getDeletionErrorHint(record)}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Seq / Par",
      key: "expected",
      width: 110,
      render: (_, record) => (
        <Text type="secondary">
          {(record.expectedSequentialRepositories ?? "—") + " / " + (record.expectedParallelRepositories ?? "—")}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => openDetailDrawer(record)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="p-4">
      <Typography.Title level={4}>Account deletion status</Typography.Title>
      <Card title="Lookup by osyncId" className="mb-4 account-deletion-card">
        <Space wrap align="start">
          <Input
            placeholder="osyncId"
            value={osyncIdLookup}
            onChange={(e) => setOsyncIdLookup(e.target.value)}
            className="hs-min-w-320-px"
            onPressEnter={handleLookupByOsyncId}
          />
          <Button type="primary" onClick={handleLookupByOsyncId} loading={isLoading}>
            Fetch status
          </Button>
        </Space>
      </Card>

      <Card
        title="Recent deletions"
        className="account-deletion-card"
        extra={<Button onClick={handleRefreshRecent}>Refresh</Button>}
      >
        <Space wrap className="account-deletion-filters mb-3">
          <Select
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              loadRecentPage(DEFAULT_ACCOUNT_DELETION_PAGE, accountDeletionTablePageSize, {
                status: value,
              });
            }}
            options={DELETION_STATUS_OPTIONS}
            className="hs-min-w-180-px"
            popupMatchSelectWidth={false}
          />
          <Input
            placeholder="Search osyncId or email"
            value={searchQueryDraft}
            onChange={(e) => setSearchQueryDraft(e.target.value)}
            onPressEnter={applyFilters}
            allowClear
            className="hs-min-w-240-px"
          />
          <Button type="primary" onClick={applyFilters} loading={isLoading}>
            Apply filters
          </Button>
        </Space>
        <Table
          size="small"
          loading={isLoading}
          columns={recentColumns}
          dataSource={recentRows}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No deletion records match your filters (or none recorded yet)."
              />
            ),
          }}
          rowClassName={(record) => {
            const s = record.status;
            if (s === "FAILED" || s === "PARTIAL_FAILURE" || record.hasFailures) {
              return "account-deletion-row hs-bg-light-red";
            }
            if (s === "COMPLETED") {
              return "account-deletion-row hs-bg-light-green1";
            }
            return "account-deletion-row";
          }}
          scroll={{ x: 1100 }}
          pagination={{
            current: accountDeletionTablePage,
            pageSize: accountDeletionTablePageSize,
            total: accountDeletionStatusTotalCount,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
            showTotal: (total) => `Total ${total} accounts`,
            onChange: handleAccountDeletionTableChange,
          }}
          onRow={(record) => ({
            onClick: () => openDetailDrawer(record),
          })}
        />
      </Card>

      <Drawer
        title="Lookup result"
        placement="right"
        width={720}
        open={lookupDrawerOpen}
        onClose={() => setLookupDrawerOpen(false)}
        destroyOnClose
      >
        {lookupDeletionStatus ? (
          <DetailDrawerContent record={lookupDeletionStatus} />
        ) : (
          <Text type="secondary">No deletion status in Redis for this osyncId.</Text>
        )}
      </Drawer>

      <Drawer
        title="Deletion details"
        placement="right"
        width={720}
        open={detailDrawerOpen}
        onClose={() => {
          setDetailDrawerOpen(false);
          setDetailRecord(null);
        }}
        destroyOnClose
      >
        <DetailDrawerContent record={detailRecord} />
      </Drawer>
    </div>
  );
}
